import {
  ArrowRight,
  Eye,
  Activity,
  Brain,
  Sparkles,
  Cog,
  Workflow,
  ShieldCheck,
  Target,
  Cloud,
  Network,
  Warehouse,
  Truck,
  ScanSearch,
  Smartphone,
  LayoutDashboard,
  Building2,
  Radio,
  Lightbulb,
  Gauge,
  CheckCircle2,
} from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import SEO from "../components/SEO";

const PRIVA_SYMBOL =
  "/images/primes4-wordmark-gradient-3-0.webp";
const PRIVA_FULL_LOGO =
  "/images/primes4-priva-full-logo-png.webp";
const PRIVA_HERO_BG =
  "/images/image-2.webp";
const ENTERPRISE_SHIFT_IMG =
  "/images/image.webp";

// --- Brand tokens ----------------------------------------------------------
const BRAND_BLUE = "#025979";
const BRAND_BLUE_DARK = "#03738C";
const AI_PURPLE = "#7C3AED";

// --- Reusable atoms (kept local so this page is self-contained) ------------
const Eyebrow = ({ children, tone = "purple" }) => (
  <p
    className={`text-[11px] font-semibold tracking-[0.22em] uppercase mb-3 ${
      tone === "blue" ? "text-[#025979]" : "text-[#7C3AED]"
    }`}
  >
    {children}
  </p>
);

const H2 = ({ children, className = "" }) => (
  <h2
    className={`text-3xl sm:text-4xl lg:text-[42px] font-bold tracking-tight text-[#0F172A] leading-[1.1] ${className}`}
    style={{ fontFamily: "Chivo, sans-serif" }}
  >
    {children}
  </h2>
);

const Lead = ({ children, className = "" }) => (
  <p className={`mt-5 text-base sm:text-lg text-[#475569] leading-relaxed max-w-3xl ${className}`}>
    {children}
  </p>
);

const SectionDivider = () => (
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div className="h-px bg-gradient-to-r from-transparent via-[#E5E7EB] to-transparent" />
  </div>
);

const PrimaryButton = ({ href, onClick, children, testId, color = "blue" }) => {
  const bg = color === "blue" ? "#2563EB" : BRAND_BLUE;
  const bgHover = color === "blue" ? "#1D4ED8" : BRAND_BLUE_DARK;
  return (
    <a
      href={href}
      onClick={onClick}
      data-testid={testId}
      className="group inline-flex items-center justify-center gap-2 rounded-sm h-11 px-6 text-xs font-semibold tracking-wide text-white transition-colors"
      style={{ backgroundColor: bg }}
      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = bgHover)}
      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = bg)}
    >
      {children}
      <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" strokeWidth={2} />
    </a>
  );
};

const OutlineButton = ({ href, onClick, children, testId, onDark = false }) => {
  if (onDark) {
    return (
      <a
        href={href}
        onClick={onClick}
        data-testid={testId}
        className="group inline-flex items-center justify-center gap-2 rounded-sm h-11 px-6 text-xs font-semibold tracking-wide border border-white/30 bg-white/5 text-white hover:bg-white/10 hover:border-white/60 transition-colors"
      >
        {children}
        <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" strokeWidth={2} />
      </a>
    );
  }
  return (
    <a
      href={href}
      onClick={onClick}
      data-testid={testId}
      className="group inline-flex items-center justify-center gap-2 rounded-sm h-11 px-6 text-xs font-semibold tracking-wide border bg-white transition-colors"
      style={{ borderColor: BRAND_BLUE, color: BRAND_BLUE }}
      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#E0F2FE")}
      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "white")}
    >
      {children}
      <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" strokeWidth={2} />
    </a>
  );
};

// --- SECTION 1: HERO -------------------------------------------------------
// Full-bleed PRIVA ecosystem background. The supplied image already carries
// the right-side PRIVA orb + ecosystem icons (Warehouse / Analytics / Transport
// / Operations / Intelligence / Automation) and the bottom warehouse + truck
// scene, so the JSX side is intentionally lean: text overlays on the left
// over a left-to-right gradient that protects legibility without darkening
// the right-side artwork.
const Hero = () => (
  <section
    data-testid="priva-hero"
    className="relative overflow-hidden text-white"
    style={{ backgroundColor: "#0A1230" }}
  >
    {/* Full background image */}
    <div
      aria-hidden="true"
      data-testid="priva-hero-bg"
      className="absolute inset-0 pointer-events-none"
      style={{
        backgroundImage: `url(${PRIVA_HERO_BG})`,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "right center",
        backgroundSize: "cover",
      }}
    />
    {/* Left-side legibility gradient — strongest at the left edge, transparent
        past the midline so the PRIVA ecosystem stays untouched. */}
    <div
      aria-hidden="true"
      className="absolute inset-0 pointer-events-none"
      style={{
        background:
          "linear-gradient(90deg, rgba(10,18,48,0.92) 0%, rgba(10,18,48,0.78) 24%, rgba(10,18,48,0.35) 50%, rgba(10,18,48,0) 72%)",
      }}
    />
    {/* Subtle enterprise grid — only over the left where the artwork is
        empty, so the right-side image isn't visually muddied. */}
    <div
      aria-hidden="true"
      className="absolute inset-0 pointer-events-none opacity-[0.08]"
      style={{
        backgroundImage:
          "linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)",
        backgroundSize: "56px 56px",
        maskImage:
          "linear-gradient(90deg, black 0%, black 35%, transparent 60%)",
        WebkitMaskImage:
          "linear-gradient(90deg, black 0%, black 35%, transparent 60%)",
      }}
    />
    {/* Hairline bottom divider — matches the home hero edge treatment. */}
    <div
      aria-hidden="true"
      className="absolute inset-x-0 bottom-0 h-px pointer-events-none"
      style={{
        background:
          "linear-gradient(90deg, transparent 0%, rgba(94,184,216,0.45) 50%, transparent 100%)",
      }}
    />

    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24 lg:pt-28 lg:pb-32 min-h-[640px] lg:min-h-[720px] flex items-center">
      <div className="grid lg:grid-cols-12 gap-12 items-center w-full">
        {/* Left copy — live HTML, vertically centred */}
        <div className="lg:col-span-7 xl:col-span-7" data-testid="priva-hero-copy">
          <p className="text-[11px] font-semibold tracking-[0.28em] uppercase text-[#A78BFA] mb-6 flex items-center gap-3">
            <span className="inline-block h-px w-8 bg-[#A78BFA]" />
            PRIVA Operational Intelligence
          </p>
          <h1
            className="text-4xl sm:text-5xl lg:text-[54px] xl:text-[58px] font-bold tracking-tight leading-[1.05] max-w-[700px]"
            style={{ fontFamily: "Chivo, sans-serif" }}
            data-testid="priva-hero-headline"
          >
            <span
              className="bg-clip-text text-transparent"
              style={{ backgroundImage: "linear-gradient(90deg, #5EB8D8 0%, #A78BFA 100%)" }}
            >
              PRIVA
            </span>
            <span> — Operational Intelligence for the Autonomous Enterprise</span>
          </h1>
          <p
            className="mt-7 text-base sm:text-lg text-white/80 leading-relaxed max-w-2xl"
            data-testid="priva-hero-sub"
          >
            PRIVA is PrimeS4 AI&apos;s operational intelligence layer designed to orchestrate enterprise execution, visibility, intelligent decision support, and AI-assisted operations across platforms and applications.
          </p>

          <div className="mt-9 flex flex-wrap gap-3" data-testid="priva-hero-ctas">
            <PrimaryButton href="/platforms" testId="priva-hero-cta-platforms" color="blue">
              Explore Platforms
            </PrimaryButton>
            <button
              type="button"
              data-testid="priva-hero-cta-demo"
              onClick={() => window.dispatchEvent(new CustomEvent("requestDemo:open"))}
              className="group inline-flex items-center justify-center gap-2 rounded-sm h-11 px-6 text-xs font-semibold tracking-wide border border-white/30 bg-white/5 text-white hover:bg-white/10 hover:border-white/60 transition-colors"
            >
              Request Demo
              <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" strokeWidth={2} />
            </button>
          </div>

          {/* Capability strip */}
          <div className="mt-14 grid sm:grid-cols-3 gap-5 max-w-2xl" data-testid="priva-hero-strip">
            {[
              { icon: ShieldCheck, label: "SAP-Aligned" },
              { icon: Brain, label: "AI-Powered" },
              { icon: Target, label: "Enterprise-Grade" },
            ].map((it) => (
              <div
                key={it.label}
                className="flex items-center gap-3 text-[12px] font-semibold tracking-[0.14em] uppercase text-white/85"
                data-testid={`priva-hero-strip-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <it.icon className="w-4 h-4 shrink-0 text-[#5EB8D8]" strokeWidth={1.5} />
                <span>{it.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right column reserved for the artwork area in the background image */}
        <div className="hidden lg:block lg:col-span-5 xl:col-span-5" aria-hidden="true" />
      </div>
    </div>
  </section>
);

// --- SECTION 2: ENTERPRISE AI SHIFT ----------------------------------------
const EnterpriseAIShift = () => (
  <section
    id="enterprise-ai-shift"
    data-testid="section-enterprise-ai-shift"
    className="py-20 lg:py-28 bg-white"
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl">
        <Eyebrow tone="purple">The Enterprise AI Shift</Eyebrow>
        <H2>From AI Assistants to the Autonomous Enterprise</H2>
        <Lead>
          Enterprise AI is evolving from conversational assistance toward operational intelligence, intelligent orchestration, and autonomous execution.
        </Lead>
      </div>
      <div className="mt-12 lg:mt-16">
        <div className="bg-white border border-[#E5E7EB] rounded-md shadow-sm overflow-hidden p-3 sm:p-5 lg:p-6">
          <img
            src={ENTERPRISE_SHIFT_IMG}
            alt="The Enterprise AI Shift — From AI Assistants to the Autonomous Enterprise"
            className="w-full h-auto block rounded-sm"
            data-testid="enterprise-shift-image"
            loading="lazy"
          />
        </div>
      </div>
    </div>
  </section>
);

// --- SECTION 3: WHAT PRIVA DOES --------------------------------------------
const CapabilityCard = ({ icon: Icon, title, desc }) => (
  <div
    className="group bg-white border border-[#E5E7EB] rounded-sm p-6 transition-all duration-200 hover:border-[#C4B5FD] hover:shadow-md hover:-translate-y-0.5"
    data-testid={`priva-cap-${title.toLowerCase().replace(/\s+/g, "-")}`}
  >
    <div
      className="w-10 h-10 rounded-sm flex items-center justify-center mb-5"
      style={{ background: "linear-gradient(135deg, #F5F3FF 0%, #EDE9FE 100%)" }}
    >
      <Icon className="w-4.5 h-4.5 text-[#7C3AED]" strokeWidth={1.6} />
    </div>
    <h3 className="text-[15px] font-semibold text-[#0F172A] leading-snug">{title}</h3>
    <p className="mt-2 text-[13px] text-[#475569] leading-relaxed">{desc}</p>
  </div>
);

const WhatPrivaDoes = () => {
  const caps = [
    { icon: Eye, title: "Unified Visibility", desc: "A single, real-time view of operations across systems and platforms." },
    { icon: Activity, title: "Intelligent Execution", desc: "Context-aware orchestration of enterprise workflows end-to-end." },
    { icon: Brain, title: "Operational Intelligence", desc: "Continuous signals, anomalies, and proactive operational insight." },
    { icon: Sparkles, title: "AI-Assisted Decisions", desc: "Contextual recommendations grounded in enterprise operations." },
    { icon: Cog, title: "Enterprise Automation", desc: "Process-level automation woven through the ecosystem." },
    { icon: Workflow, title: "Autonomous Workflows", desc: "Self-driving workflows that execute, adapt, and improve over time." },
  ];
  return (
    <section
      id="what-priva-does"
      data-testid="section-what-priva-does"
      className="relative py-20 lg:py-28 bg-gradient-to-b from-white via-[#FAFAFF] to-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <Eyebrow>What PRIVA Does</Eyebrow>
          <H2>Operational Intelligence Layer</H2>
          <Lead>
            PRIVA connects operational visibility, execution intelligence, automation, and contextual decision support across enterprise workflows.
          </Lead>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="priva-caps-grid">
          {caps.map((c) => (
            <CapabilityCard key={c.title} icon={c.icon} title={c.title} desc={c.desc} />
          ))}
        </div>
      </div>
    </section>
  );
};

// --- SECTION 4: HOW PRIVA WORKS --------------------------------------------
const HowPrivaWorks = () => {
  const stages = [
    { label: "Signals",       title: "Operational Signals",        desc: "Live telemetry from platforms, apps, and SAP systems.",     icon: Radio },
    { label: "Intelligence",  title: "AI Reasoning",                desc: "Contextual reasoning over signals, history, and patterns.", icon: Brain },
    { label: "Decisions",     title: "Contextual Recommendations",  desc: "Grounded recommendations tied to operational state.",       icon: Lightbulb },
    { label: "Actions",       title: "Workflow Orchestration",      desc: "Coordinates the right action across the right system.",     icon: Workflow },
    { label: "Execution",     title: "Intelligent Execution",       desc: "Drives execution autonomously within enterprise guardrails.", icon: Activity },
    { label: "Outcomes",      title: "Business Outcomes",           desc: "Measurable, governed impact across operations.",            icon: Target },
  ];
  return (
    <section
      id="how-priva-works"
      data-testid="section-how-priva-works"
      className="relative py-20 lg:py-28 bg-[#0B1734] text-white overflow-hidden"
    >
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage: "radial-gradient(ellipse at center, black 60%, transparent 95%)",
          WebkitMaskImage: "radial-gradient(ellipse at center, black 60%, transparent 95%)",
        }}
      />
      <div className="absolute -top-32 right-1/4 w-[420px] h-[420px] rounded-full blur-3xl opacity-20 pointer-events-none"
        style={{ background: "radial-gradient(circle, #7C3AED 0%, transparent 65%)" }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <p className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#A78BFA] mb-3">
            How PRIVA Works
          </p>
          <h2
            className="text-3xl sm:text-4xl lg:text-[42px] font-bold tracking-tight leading-[1.1]"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            Built for Intelligent Enterprise Execution
          </h2>
          <p className="mt-5 text-base sm:text-lg text-white/70 leading-relaxed">
            Signals flow into PRIVA, intelligence shapes them into context, decisions become orchestrated actions, and execution produces measurable outcomes.
          </p>
        </div>

        {/* Stage strip — Signals → Intelligence → Decisions → Actions → Outcomes */}
        <div
          className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3"
          data-testid="priva-stages"
        >
          {["Signals", "Intelligence", "Decisions", "Actions", "Outcomes"].map((label, i) => (
            <div key={label} className="flex items-center gap-3">
              <div
                className="flex-1 px-4 py-3 rounded-sm border text-center"
                style={{
                  borderColor: "rgba(94,184,216,0.35)",
                  background:
                    "linear-gradient(180deg, rgba(94,184,216,0.08) 0%, rgba(124,58,237,0.08) 100%)",
                }}
                data-testid={`priva-stage-${label.toLowerCase()}`}
              >
                <div className="text-[10px] font-semibold tracking-[0.2em] uppercase text-[#A78BFA] mb-1">
                  Stage {i + 1}
                </div>
                <div className="text-[14px] font-semibold text-white">{label}</div>
              </div>
              {i < 4 && (
                <ArrowRight className="hidden lg:block w-4 h-4 text-white/35 shrink-0" strokeWidth={1.6} />
              )}
            </div>
          ))}
        </div>

        {/* Detailed cards */}
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="priva-stage-cards">
          {stages.map((s) => (
            <div
              key={s.title}
              className="relative bg-white/[0.04] border border-white/10 rounded-sm p-6 hover:bg-white/[0.07] transition-colors"
              data-testid={`priva-stage-card-${s.label.toLowerCase()}`}
            >
              <div className="flex items-center gap-2 text-[10px] font-semibold tracking-[0.2em] uppercase text-[#5EB8D8] mb-3">
                <s.icon className="w-4 h-4" strokeWidth={1.6} />
                {s.label}
              </div>
              <div className="text-[16px] font-semibold text-white">{s.title}</div>
              <p className="mt-2 text-[13px] text-white/65 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// --- SECTION 5: SAP-ALIGNED FOUNDATION -------------------------------------
const SapFoundation = () => {
  const items = [
    { icon: Cloud,       label: "SAP BTP",                desc: "Built on SAP Business Technology Platform foundations.", sap: true },
    { icon: Sparkles,    label: "SAP Business AI",        desc: "Aligned with SAP Business AI principles and patterns.",  sap: true },
    { icon: CheckCircle2,label: "Joule Alignment",        desc: "Integrates with SAP Joule for cross-system intelligence.", sap: true },
    { icon: Network,     label: "Enterprise Integrations",desc: "Connects across enterprise systems and ecosystems.",     sap: false },
    { icon: Workflow,    label: "AI Orchestration",       desc: "Orchestrates AI models, agents, and workflows.",          sap: false },
    { icon: ShieldCheck, label: "Governance & Security",  desc: "Enterprise-grade governance, controls, and observability.", sap: false },
  ];
  return (
    <section
      id="sap-foundation"
      data-testid="section-sap-foundation"
      className="py-20 lg:py-28 bg-[#F8FAFC] border-y border-[#E5E7EB]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <Eyebrow tone="blue">SAP-Aligned Foundation</Eyebrow>
          <H2>SAP-Aligned. Enterprise-Ready.</H2>
          <Lead>
            Built to align with SAP-native enterprise transformation, operational orchestration, and intelligent business execution.
          </Lead>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="priva-sap-grid">
          {items.map((it) => (
            <div
              key={it.label}
              className={`group bg-white border rounded-sm p-6 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md ${
                it.sap ? "border-[#BAE6FD] hover:border-[#0093cb]" : "border-[#E5E7EB] hover:border-[#C4B5FD]"
              }`}
              data-testid={`priva-sap-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              <div
                className="w-10 h-10 rounded-sm flex items-center justify-center mb-5"
                style={{
                  background: it.sap
                    ? "linear-gradient(135deg, #E0F2FE 0%, #BAE6FD 100%)"
                    : "linear-gradient(135deg, #F5F3FF 0%, #EDE9FE 100%)",
                }}
              >
                <it.icon className={`w-4.5 h-4.5 ${it.sap ? "text-[#025979]" : "text-[#7C3AED]"}`} strokeWidth={1.6} />
              </div>
              <div className="flex items-center gap-2">
                <h3 className="text-[15px] font-semibold text-[#0F172A]">{it.label}</h3>
                {it.sap && (
                  <span className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#025979]">
                    SAP
                  </span>
                )}
              </div>
              <p className="mt-2 text-[13px] text-[#475569] leading-relaxed">{it.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// --- SECTION 6: PLATFORM CONNECTION ----------------------------------------
const PlatformConnection = () => {
  const nodes = [
    { label: "WarehouseX",   icon: Warehouse,        href: "/warehousex",                                color: "blue"   },
    { label: "TransportX",   icon: Truck,            href: null, comingSoon: true,                       color: "blue"   },
    { label: "YardX",        icon: Building2,        href: null, comingSoon: true,                       color: "blue"   },
    { label: "SmartInspect", icon: ScanSearch,       href: "/warehousex/smartintegrations/smartinspect", color: "purple" },
    { label: "SmartRF",      icon: Smartphone,       href: "/warehousex/smartexecution/smartrf",         color: "purple" },
    { label: "SmartCenter",  icon: LayoutDashboard,  href: null, comingSoon: true,                       color: "purple" },
  ];
  return (
    <section
      id="platform-connection"
      data-testid="section-platform-connection"
      className="py-20 lg:py-28 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <Eyebrow>Ecosystem Connectivity</Eyebrow>
          <H2>Powering the PrimeS4 AI Ecosystem</H2>
          <Lead>
            PRIVA threads operational intelligence through every PrimeS4 AI platform and enterprise application.
          </Lead>
        </div>

        {/* Hub + nodes layout */}
        <div className="mt-12 grid lg:grid-cols-12 gap-8 items-center" data-testid="priva-ecosystem">
          {/* Hub */}
          <div className="lg:col-span-4 flex items-center justify-center">
            <div
              className="relative w-56 h-56 rounded-full flex items-center justify-center border"
              style={{
                background: "radial-gradient(circle at 50% 50%, #F5F3FF 0%, #FFFFFF 70%)",
                borderColor: "#E0E7FF",
                boxShadow: "0 0 40px rgba(124,58,237,0.18)",
              }}
            >
              <img loading="lazy"
                src={PRIVA_SYMBOL}
                alt="PRIVA"
                className="w-28 h-auto object-contain"
                data-testid="priva-ecosystem-symbol"
              />
              <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-sm bg-white border border-[#E5E7EB] text-[10px] font-semibold tracking-[0.18em] uppercase text-[#7C3AED]">
                PRIVA
              </div>
            </div>
          </div>

          {/* Nodes */}
          <div className="lg:col-span-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {nodes.map((n) => {
              const isLink = !!n.href && !n.comingSoon;
              const Wrap = isLink ? "a" : "div";
              return (
                <Wrap
                  key={n.label}
                  href={isLink ? n.href : undefined}
                  data-testid={`priva-eco-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`group flex items-center gap-3 px-5 py-4 rounded-sm border bg-white transition-all duration-200 ${
                    isLink ? "hover:-translate-y-0.5 hover:shadow-md hover:border-[#5EB8D8]" : ""
                  } ${n.comingSoon ? "border-[#E5E7EB]" : n.color === "blue" ? "border-[#BAE6FD]" : "border-[#E5E7EB]"}`}
                >
                  <div
                    className="w-9 h-9 rounded-sm flex items-center justify-center shrink-0"
                    style={{
                      background:
                        n.color === "blue"
                          ? "linear-gradient(135deg, #E0F2FE 0%, #BAE6FD 100%)"
                          : "linear-gradient(135deg, #F5F3FF 0%, #EDE9FE 100%)",
                    }}
                  >
                    <n.icon
                      className={`w-4 h-4 ${n.color === "blue" ? "text-[#025979]" : "text-[#7C3AED]"}`}
                      strokeWidth={1.6}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[14px] font-semibold text-[#0F172A] truncate">{n.label}</div>
                    {n.comingSoon && (
                      <div className="text-[11px] italic text-[#9CA3AF]">Coming soon</div>
                    )}
                  </div>
                  {isLink && (
                    <ArrowRight className="w-3.5 h-3.5 text-[#025979] opacity-0 group-hover:opacity-100 transition-opacity" strokeWidth={2} />
                  )}
                </Wrap>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

// --- SECTION 7: CLOSING CTA ------------------------------------------------
const ClosingCta = () => (
  <section
    data-testid="section-priva-closing"
    className="relative py-24 lg:py-28 text-white overflow-hidden"
    style={{
      background:
        "linear-gradient(135deg, #0E1A3F 0%, #112452 50%, #0B1734 100%)",
    }}
  >
    <div className="absolute inset-0 pointer-events-none">
      <div className="absolute top-0 left-1/3 w-[420px] h-[420px] rounded-full blur-3xl opacity-20"
        style={{ background: "radial-gradient(circle, #025979 0%, transparent 60%)" }} />
      <div className="absolute bottom-0 right-1/4 w-[360px] h-[360px] rounded-full blur-3xl opacity-15"
        style={{ background: "radial-gradient(circle, #7C3AED 0%, transparent 60%)" }} />
    </div>
    <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <p className="text-[11px] font-semibold tracking-[0.28em] uppercase text-[#A78BFA] mb-4 flex items-center justify-center gap-3">
        <span className="inline-block h-px w-8 bg-[#A78BFA]" />
        Get Started
        <span className="inline-block h-px w-8 bg-[#A78BFA]" />
      </p>
      <h2
        className="text-3xl sm:text-4xl lg:text-[44px] font-bold tracking-tight leading-[1.1]"
        style={{ fontFamily: "Chivo, sans-serif" }}
      >
        Build the Autonomous Enterprise with PRIVA
      </h2>
      <p className="mt-5 text-base sm:text-lg text-white/70 leading-relaxed max-w-3xl mx-auto">
        Operational intelligence, intelligent orchestration, and AI-assisted execution designed for modern enterprise operations.
      </p>
      <div className="mt-9 flex flex-wrap justify-center gap-3">
        <button
          type="button"
          data-testid="priva-closing-cta-demo"
          onClick={() => window.dispatchEvent(new CustomEvent("requestDemo:open"))}
          className="inline-flex items-center justify-center gap-2 rounded-sm h-11 px-7 text-xs font-semibold tracking-wide bg-[#2563EB] hover:bg-[#1D4ED8] text-white transition-colors"
        >
          Request Demo
          <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
        </button>
        <OutlineButton href="/platforms" testId="priva-closing-cta-platforms" onDark>
          Explore Platforms
        </OutlineButton>
      </div>
    </div>
  </section>
);

const PrivaPage = () => (
  <div className="min-h-screen flex flex-col bg-white" data-testid="priva-page">
    <SEO
      title="PRIVA | Operational Intelligence for Enterprise Execution"
      description="PRIVA is PrimeS4's AI orchestration engine — turning SAP, supply chain and operational data into real-time decisions, recommendations, and execution intelligence."
      keywords={[
        "PRIVA",
        "Operational Intelligence",
        "AI orchestration",
        "Enterprise execution intelligence",
        "SAP AI",
        "SAP BTP AI",
        "Intelligent Enterprise",
        "Supply chain AI",
      ]}
      canonical="/priva"
      imageAlt="PRIVA operational intelligence platform"
    />
    <Header />
    <main className="flex-1">
      <Hero />
      <EnterpriseAIShift />
      <SectionDivider />
      <WhatPrivaDoes />
      <HowPrivaWorks />
      <SapFoundation />
      <PlatformConnection />
      <ClosingCta />
    </main>
    <Footer />
  </div>
);

export default PrivaPage;
